import { z } from 'zod';
import { apiRequest } from '@/api/client';
import {
  envelopeSchema,
  leagueDuelPickSchema,
  leagueGwSummarySchema,
  leagueMemberSchema,
  leagueSchema,
  myLeagueItemSchema,
  paginationSchema,
  publicLeagueItemSchema,
  type CreateLeagueBody,
  type JoinByCodeBody,
  type League,
  type LeagueDuelPick,
  type LeagueDuelPicksTab,
  type LeagueGwSummary,
  type LeagueGwSummaryQuery,
  type LeagueMember,
  type LeagueMembersQuery,
  type MyLeagueItem,
  type Pagination,
  type PublicLeagueItem,
  type PublicLeaguesQuery,
  type UpdateLeagueBody,
} from '@/types/leagues';

const BASE = '/api/v1/leagues';

function parseEnvelope<T>(schema: z.ZodType<{ success: true; data: T }>, raw: unknown): T {
  return schema.parse(raw).data;
}

const myLeaguesEnvelope = envelopeSchema(z.object({ leagues: z.array(myLeagueItemSchema) }));
const leagueEnvelope = envelopeSchema(z.object({ league: leagueSchema }));
const leagueMembersEnvelope = envelopeSchema(
  z.object({
    members: z.array(leagueMemberSchema),
    pagination: paginationSchema,
  }),
);
const publicLeaguesEnvelope = envelopeSchema(
  z.object({
    leagues: z.array(publicLeagueItemSchema),
    pagination: paginationSchema,
  }),
);
const createLeagueEnvelope = envelopeSchema(
  z.object({
    league: leagueSchema,
    invite_code: z.string(),
  }),
);
const joinEnvelope = envelopeSchema(
  z.object({
    league: leagueSchema,
    already_member: z.boolean(),
  }),
);
const inviteCodeEnvelope = envelopeSchema(z.object({ invite_code: z.string() }));
const deletedEnvelope = envelopeSchema(z.object({ deleted: z.literal(true) }));
const leftEnvelope = envelopeSchema(z.object({ left: z.literal(true) }));
const removedEnvelope = envelopeSchema(z.object({ removed: z.literal(true) }));
const gwSummaryEnvelope = envelopeSchema(leagueGwSummarySchema);
const duelPicksPageEnvelope = envelopeSchema(
  z.object({
    picks: z.array(leagueDuelPickSchema),
    pagination: z.object({
      offset: z.number(),
      limit: z.number(),
      total_count: z.number(),
    }),
  }),
);

export async function getMyLeagues(): Promise<MyLeagueItem[]> {
  const res = await apiRequest<unknown>({ url: `${BASE}/me`, method: 'GET' });
  return parseEnvelope(myLeaguesEnvelope, res).leagues;
}

export async function getLeagueById(id: string): Promise<League> {
  const res = await apiRequest<unknown>({ url: `${BASE}/${encodeURIComponent(id)}`, method: 'GET' });
  return parseEnvelope(leagueEnvelope, res).league;
}

export async function getLeagueMembers(
  id: string,
  params?: LeagueMembersQuery,
): Promise<{ members: LeagueMember[]; pagination: Pagination }> {
  const sp = new URLSearchParams();
  if (params?.limit !== undefined) sp.set('limit', String(params.limit));
  if (params?.offset !== undefined) sp.set('offset', String(params.offset));
  const q = sp.toString();
  const url = `${BASE}/${encodeURIComponent(id)}/members${q ? `?${q}` : ''}`;
  const res = await apiRequest<unknown>({ url, method: 'GET' });
  return parseEnvelope(leagueMembersEnvelope, res);
}

export async function getPublicLeagues(params: PublicLeaguesQuery): Promise<{
  leagues: PublicLeagueItem[];
  pagination: Pagination;
}> {
  const sp = new URLSearchParams();
  if (params.search !== undefined && params.search.trim() !== '') sp.set('search', params.search.trim());
  if (params.limit !== undefined) sp.set('limit', String(params.limit));
  if (params.offset !== undefined) sp.set('offset', String(params.offset));
  const q = sp.toString();
  const url = `${BASE}/public${q ? `?${q}` : ''}`;
  const res = await apiRequest<unknown>({ url, method: 'GET' });
  return parseEnvelope(publicLeaguesEnvelope, res);
}

export async function getLeagueByInviteCode(code: string): Promise<League> {
  const res = await apiRequest<unknown>({
    url: `${BASE}/by-invite-code/${encodeURIComponent(code)}`,
    method: 'GET',
  });
  return parseEnvelope(leagueEnvelope, res).league;
}

export async function createLeague(body: CreateLeagueBody): Promise<{ league: League; invite_code: string }> {
  const res = await apiRequest<unknown>({ url: BASE, method: 'POST', data: body });
  return parseEnvelope(createLeagueEnvelope, res);
}

export async function updateLeague(id: string, body: UpdateLeagueBody): Promise<League> {
  const res = await apiRequest<unknown>({
    url: `${BASE}/${encodeURIComponent(id)}`,
    method: 'PATCH',
    data: body,
  });
  return parseEnvelope(leagueEnvelope, res).league;
}

export async function deleteLeague(id: string): Promise<void> {
  const res = await apiRequest<unknown>({ url: `${BASE}/${encodeURIComponent(id)}`, method: 'DELETE' });
  parseEnvelope(deletedEnvelope, res);
}

export async function joinLeague(id: string): Promise<{ league: League; already_member: boolean }> {
  const res = await apiRequest<unknown>({ url: `${BASE}/${encodeURIComponent(id)}/join`, method: 'POST' });
  return parseEnvelope(joinEnvelope, res);
}

export async function joinByCode(body: JoinByCodeBody): Promise<{ league: League; already_member: boolean }> {
  const res = await apiRequest<unknown>({ url: `${BASE}/join-by-code`, method: 'POST', data: body });
  return parseEnvelope(joinEnvelope, res);
}

export async function leaveLeague(id: string): Promise<void> {
  const res = await apiRequest<unknown>({
    url: `${BASE}/${encodeURIComponent(id)}/members/me`,
    method: 'DELETE',
  });
  parseEnvelope(leftEnvelope, res);
}

export async function removeMember(id: string, memberUserId: string): Promise<void> {
  const res = await apiRequest<unknown>({
    url: `${BASE}/${encodeURIComponent(id)}/members/${encodeURIComponent(memberUserId)}`,
    method: 'DELETE',
  });
  parseEnvelope(removedEnvelope, res);
}

export async function regenerateInviteCode(id: string): Promise<string> {
  const res = await apiRequest<unknown>({
    url: `${BASE}/${encodeURIComponent(id)}/regenerate-invite-code`,
    method: 'POST',
  });
  return parseEnvelope(inviteCodeEnvelope, res).invite_code;
}

export async function getLeagueGwSummary(id: string, params?: LeagueGwSummaryQuery): Promise<LeagueGwSummary> {
  const sp = new URLSearchParams();
  if (params?.gameweek_id) sp.set('gameweek_id', params.gameweek_id);
  if (params?.limit !== undefined) sp.set('limit', String(params.limit));
  if (params?.offset !== undefined) sp.set('offset', String(params.offset));
  const q = sp.toString();
  const url = `${BASE}/${encodeURIComponent(id)}/gw-summary${q ? `?${q}` : ''}`;
  const res = await apiRequest<unknown>({ url, method: 'GET' });
  return parseEnvelope(gwSummaryEnvelope, res);
}

export async function getLeagueDuelPicks(
  id: string,
  duelSlug: string,
  params: { pick: LeagueDuelPicksTab; limit?: number; offset?: number },
): Promise<{
  picks: LeagueDuelPick[];
  pagination: { offset: number; limit: number; total_count: number };
}> {
  const sp = new URLSearchParams();
  sp.set('pick', params.pick);
  if (params.limit !== undefined) sp.set('limit', String(params.limit));
  if (params.offset !== undefined) sp.set('offset', String(params.offset));
  const qs = sp.toString();
  const res = await apiRequest<unknown>({
    url: `${BASE}/${encodeURIComponent(id)}/duels/${encodeURIComponent(duelSlug)}/picks${qs ? `?${qs}` : ''}`,
    method: 'GET',
  });
  return parseEnvelope(duelPicksPageEnvelope, res);
}
